import type { Secret, SecretAction, SecretState } from "./types";

export const initialSecretState: SecretState = {
  secrets: [],
  loading: new Set(),
  editing: new Set(),
  visible: new Set(),
  errors: new Map(),
  operationsPending: new Set(),
};

export function secretReducer(
  state: SecretState,
  action: SecretAction,
): SecretState {
  switch (action.type) {
    case "SET_SECRETS":
      return {
        ...state,
        secrets: action.secrets,
        // Clear loading states for secrets that no longer exist
        loading: new Set(
          [...state.loading].filter((key) =>
            action.secrets.some((secret) => secret.key === key),
          ),
        ),
        editing: new Set(
          [...state.editing].filter((key) =>
            action.secrets.some((secret) => secret.key === key),
          ),
        ),
        visible: new Set(
          [...state.visible].filter((key) =>
            action.secrets.some((secret) => secret.key === key),
          ),
        ),
        errors: new Map(
          [...state.errors].filter(([key]) =>
            action.secrets.some((secret) => secret.key === key),
          ),
        ),
      };

    case "ADD_SECRET":
      return {
        ...state,
        secrets: [...state.secrets, action.secret],
      };

    case "REMOVE_SECRET": {
      const secretToRemove = state.secrets[action.index];
      if (!secretToRemove) {
        return state;
      }

      const newSecrets = state.secrets.filter(
        (_, index) => index !== action.index,
      );
      return {
        ...state,
        secrets: newSecrets,
        loading: new Set(
          [...state.loading].filter((key) => key !== secretToRemove.key),
        ),
        editing: new Set(
          [...state.editing].filter((key) => key !== secretToRemove.key),
        ),
        visible: new Set(
          [...state.visible].filter((key) => key !== secretToRemove.key),
        ),
        errors: new Map(
          [...state.errors].filter(([key]) => key !== secretToRemove.key),
        ),
        operationsPending: new Set(
          [...state.operationsPending].filter(
            (op) => !op.startsWith(`${secretToRemove.key}:`),
          ),
        ),
      };
    }

    case "UPDATE_SECRET": {
      const newSecrets = [...state.secrets];
      if (newSecrets[action.index]) {
        newSecrets[action.index] = {
          ...newSecrets[action.index],
          ...action.updates,
        };
      }
      return {
        ...state,
        secrets: newSecrets,
      };
    }

    case "START_OPERATION": {
      const operationKey = `${action.key}:${action.operation}`;
      return {
        ...state,
        loading: new Set([...state.loading, action.key]),
        operationsPending: new Set([...state.operationsPending, operationKey]),
        errors: new Map(
          [...state.errors].filter(([key]) => key !== action.key),
        ),
      };
    }

    case "COMPLETE_OPERATION": {
      const operationKey = `${action.key}:${action.operation}`;
      return {
        ...state,
        loading: new Set(
          [...state.loading].filter((key) => key !== action.key),
        ),
        operationsPending: new Set(
          [...state.operationsPending].filter((op) => op !== operationKey),
        ),
        editing:
          action.operation === "save"
            ? new Set([...state.editing].filter((key) => key !== action.key))
            : state.editing,
      };
    }

    case "FAIL_OPERATION": {
      const operationKey = `${action.key}:${action.operation}`;
      return {
        ...state,
        loading: new Set(
          [...state.loading].filter((key) => key !== action.key),
        ),
        operationsPending: new Set(
          [...state.operationsPending].filter((op) => op !== operationKey),
        ),
        errors: new Map([...state.errors, [action.key, action.error]]),
      };
    }

    case "START_EDIT":
      return {
        ...state,
        editing: new Set([...state.editing, action.key]),
        errors: new Map(
          [...state.errors].filter(([key]) => key !== action.key),
        ),
      };

    case "STOP_EDIT":
      return {
        ...state,
        editing: new Set(
          [...state.editing].filter((key) => key !== action.key),
        ),
      };

    case "TOGGLE_VISIBILITY": {
      const newVisible = new Set(state.visible);
      if (newVisible.has(action.key)) {
        newVisible.delete(action.key);
      } else {
        newVisible.add(action.key);
      }
      return {
        ...state,
        visible: newVisible,
      };
    }

    case "CLEAR_ERROR":
      return {
        ...state,
        errors: new Map(
          [...state.errors].filter(([key]) => key !== action.key),
        ),
      };

    case "RESET_STATE":
      return initialSecretState;

    default:
      return state;
  }
}

// Selector functions for easier state access
export const secretSelectors = {
  getSecretByKey: (state: SecretState, key: string): Secret | undefined =>
    state.secrets.find((secret) => secret.key === key),

  getSecretByIndex: (state: SecretState, index: number): Secret | undefined =>
    state.secrets[index],

  isLoading: (state: SecretState, key: string): boolean =>
    state.loading.has(key),

  isEditing: (state: SecretState, key: string): boolean =>
    state.editing.has(key),

  isVisible: (state: SecretState, key: string): boolean =>
    state.visible.has(key),

  getError: (state: SecretState, key: string): string | undefined =>
    state.errors.get(key),

  hasError: (state: SecretState, key: string): boolean => state.errors.has(key),

  isOperationPending: (
    state: SecretState,
    key: string,
    operation: string,
  ): boolean => state.operationsPending.has(`${key}:${operation}`),

  hasAnyPendingOperations: (state: SecretState): boolean =>
    state.operationsPending.size > 0,

  getNewSecrets: (state: SecretState): Secret[] =>
    state.secrets.filter((secret) => !secret.isExisting),

  getExistingSecrets: (state: SecretState): Secret[] =>
    state.secrets.filter((secret) => secret.isExisting),
};
